import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Save, Send, Link as LinkIcon, MessageCircle, Copy, Upload, X, QrCode } from "lucide-react";
import { toast } from "sonner";
import { api, API, loadAuthToken } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

const AdminSettingsPage = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!loadAuthToken()) { navigate("/admin/login"); return; }
    load();
    // eslint-disable-next-line
  }, []);

  const load = async () => {
    try {
      const r = await api.get("/admin/settings");
      setSettings(r.data || {});
    } catch (e) {
      if (e?.response?.status === 401) navigate("/admin/login");
      else if (e?.response?.status === 403) { toast.error("Super Admin only"); navigate("/admin"); }
      else toast.error("Failed");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch("/admin/settings", settings);
      setSettings(r.data);
      toast.success("Saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const testTelegram = async () => {
    try { await api.post("/admin/settings/telegram/test"); toast.success("Test message sent — check Telegram"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const setupWebhook = async () => {
    try {
      const r = await api.post("/admin/settings/telegram/setup-webhook");
      toast.success(`Webhook registered: ${r.data.webhook_url}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!settings) return <div className="text-center py-12 text-slate-500">Loading…</div>;

  const set = (k, v) => setSettings({ ...settings, [k]: v });

  return (
    <div data-testid="admin-settings-page" className="max-w-3xl mx-auto px-4 lg:px-8 py-8">
      <Button variant="ghost" size="sm" onClick={() => navigate("/admin")} className="mb-4"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
      <h1 className="text-2xl sm:text-3xl font-heading font-semibold text-slate-900">Settings</h1>
      <p className="text-sm text-slate-500 mt-1">UPI / bank details for store purchases + Telegram bot for instant admin alerts and remote control.</p>

      <Tabs defaultValue="payment" className="mt-6">
        <TabsList>
          <TabsTrigger value="payment" data-testid="tab-payment">Payment Details</TabsTrigger>
          <TabsTrigger value="telegram" data-testid="tab-telegram">Telegram Bot</TabsTrigger>
          <TabsTrigger value="wa" data-testid="tab-wa-templates">WhatsApp Templates</TabsTrigger>
        </TabsList>

        <TabsContent value="payment">
          <div className="rounded-lg bg-white border border-slate-200 p-6 space-y-5 mt-3">
            <div>
              <Label className="font-semibold">Payment QR Code</Label>
              <p className="text-xs text-slate-500 mb-2">Yeh QR customer ko Buy Now dabate hi dikhega. UPI app scan karke pay karega.</p>
              <QrUploader onChange={load} />
            </div>

            <div className="border-t pt-4">
              <Label className="font-semibold">UPI / Bank (also shown to buyer)</Label>
              <div className="grid grid-cols-2 gap-3 text-sm mt-2">
                <div className="col-span-2 sm:col-span-1"><Label>UPI ID</Label><Input value={settings.upi_id || ""} onChange={(e) => set("upi_id", e.target.value)} placeholder="ranjit@upi" data-testid="upi-id" /></div>
                <div className="col-span-2 sm:col-span-1"><Label>UPI Display Name</Label><Input value={settings.upi_name || ""} onChange={(e) => set("upi_name", e.target.value)} placeholder="Ranjit Kumar Sharma" /></div>
                <div className="col-span-2"><Label>Bank Name</Label><Input value={settings.bank_name || ""} onChange={(e) => set("bank_name", e.target.value)} placeholder="State Bank of India" /></div>
                <div><Label>Account Number</Label><Input value={settings.bank_account || ""} onChange={(e) => set("bank_account", e.target.value)} data-testid="bank-account" /></div>
                <div><Label>IFSC</Label><Input value={settings.bank_ifsc || ""} onChange={(e) => set("bank_ifsc", e.target.value)} placeholder="SBIN0001234" /></div>
                <div className="col-span-2"><Label>Account Holder Name</Label><Input value={settings.bank_holder || ""} onChange={(e) => set("bank_holder", e.target.value)} /></div>
              </div>
            </div>
            <Button onClick={save} disabled={saving} className="bg-teal-700 hover:bg-teal-800 text-white" data-testid="save-payment-btn"><Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save Payment Details"}</Button>
          </div>
        </TabsContent>

        <TabsContent value="telegram">
          <div className="rounded-lg bg-white border border-slate-200 p-6 space-y-4 mt-3">
            <div className="rounded-md bg-blue-50 border border-blue-200 p-3 text-sm text-slate-800 space-y-2">
              <div className="font-semibold text-blue-800">📱 Kaise setup karein?</div>
              <div className="text-xs leading-relaxed">
                1. Telegram me <a href="https://t.me/BotFather" target="_blank" rel="noreferrer" className="text-blue-700 underline">@BotFather</a> khol ke <code>/newbot</code> chalayein — bot ka naam aur username dijiye.<br />
                2. BotFather aapko <b>Bot Token</b> dega — copy karke niche paste karein.<br />
                3. Apne naye bot ko khol ke <code>/start</code> dabayein.<br />
                4. Phir <a href="https://t.me/userinfobot" target="_blank" rel="noreferrer" className="text-blue-700 underline">@userinfobot</a> se apna <b>Chat ID</b> nikaal ke niche paste karein.<br />
                5. Save karein → "Test Alert" dabayein → Telegram me message aana chahiye.<br />
                6. "Register Webhook" dabayein — ab har naya request / order aapke Telegram pe alert bhejega with quick action buttons.
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 text-sm">
              <div><Label>Bot Token</Label><Input type="password" value={settings.telegram_bot_token || ""} onChange={(e) => set("telegram_bot_token", e.target.value)} placeholder="123456:ABC-DEF…" data-testid="tg-token" /></div>
              <div><Label>Your Telegram Chat ID</Label><Input value={settings.telegram_chat_id || ""} onChange={(e) => set("telegram_chat_id", e.target.value)} placeholder="123456789" data-testid="tg-chat-id" /></div>
              {settings.telegram_webhook_secret && (
                <div className="text-xs text-slate-500">
                  Webhook secret: <code className="bg-slate-100 px-2 py-0.5 rounded">{settings.telegram_webhook_secret.slice(0, 8)}…</code>
                  <button className="ml-2 text-teal-700" onClick={() => { navigator.clipboard.writeText(settings.telegram_webhook_secret); toast.success("Copied"); }}><Copy className="w-3 h-3 inline" /></button>
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-2">
              <Button onClick={save} disabled={saving} className="bg-teal-700 hover:bg-teal-800 text-white" data-testid="save-tg-btn"><Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save"}</Button>
              <Button variant="outline" onClick={testTelegram} disabled={!settings.telegram_bot_token || !settings.telegram_chat_id} data-testid="test-tg-btn"><Send className="w-4 h-4 mr-2" /> Test Alert</Button>
              <Button variant="outline" onClick={setupWebhook} disabled={!settings.telegram_bot_token} data-testid="setup-webhook-btn"><LinkIcon className="w-4 h-4 mr-2" /> Register Webhook</Button>
            </div>

            <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-xs text-slate-700 leading-relaxed">
              <div className="font-semibold mb-1 flex items-center gap-1"><MessageCircle className="w-3 h-3" /> Bot commands (webhook register hone ke baad)</div>
              <ul className="space-y-0.5">
                <li><code>/help</code> — commands list</li>
                <li><code>/status AP-A1B2 In Process</code> — request status change</li>
                <li><code>/order AP-A8240833 paid</code> — order verify (or "reject")</li>
                <li><code>/pending</code> — pending orders</li>
                <li><code>/leads</code> — last 5 leads</li>
                <li>Ya har alert message pe <b>buttons dikhenge</b> — tap karke instant action.</li>
              </ul>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="wa"><WaTemplatesEditor /></TabsContent>
      </Tabs>
    </div>
  );
};

const WaTemplatesEditor = () => {
  const [defaults, setDefaults] = useState({});
  const [tpls, setTpls] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/wa-templates").then((r) => {
      setDefaults(r.data.defaults || {});
      setTpls({ ...r.data.defaults, ...(r.data.templates || {}) });
    }).catch(() => toast.error("Templates load failed"));
  }, []);

  const save = async () => {
    setSaving(true);
    try { await api.patch("/admin/wa-templates", { templates: tpls }); toast.success("Templates saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="rounded-lg bg-white border border-slate-200 p-6 space-y-5 mt-3" data-testid="wa-templates-editor">
      <div className="rounded-md bg-green-50 border border-green-200 p-3 text-xs text-slate-700 leading-relaxed">
        Status change par ye message WhatsApp me khulega. Ye placeholders use karein: <code>{"{name}"}</code> <code>{"{service}"}</code> <code>{"{request_id}"}</code> <code>{"{track_link}"}</code>
      </div>
      {Object.keys(defaults).map((st) => (
        <div key={st}>
          <div className="flex items-center justify-between mb-1">
            <Label className="font-semibold">{st}</Label>
            <button type="button" className="text-xs text-teal-700 hover:underline" onClick={() => setTpls({ ...tpls, [st]: defaults[st] })} data-testid={`wa-reset-${st.replace(/\s+/g, "-").toLowerCase()}`}>Default reset</button>
          </div>
          <Textarea rows={5} value={tpls[st] || ""} onChange={(e) => setTpls({ ...tpls, [st]: e.target.value })} data-testid={`wa-tpl-${st.replace(/\s+/g, "-").toLowerCase()}`} />
        </div>
      ))}
      <Button onClick={save} disabled={saving} className="bg-green-600 hover:bg-green-700 text-white" data-testid="save-wa-templates-btn"><Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save Templates"}</Button>
    </div>
  );
};

export default AdminSettingsPage;

const QrUploader = ({ onChange }) => {
  const [busy, setBusy] = useState(false);
  const [hasQr, setHasQr] = useState(false);
  const [bump, setBump] = useState(Date.now());
  const inp = useRef(null);

  useEffect(() => {
    api.get("/settings/public").then((r) => setHasQr(!!r.data.has_qr));
  }, []);

  const upload = async (file) => {
    if (!file) return;
    if (!/\.(jpe?g|png|webp)$/i.test(file.name)) { toast.error("Image only (JPG/PNG/WEBP)"); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post("/admin/settings/qr-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("QR uploaded");
      setHasQr(true); setBump(Date.now()); onChange && onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("Remove QR code?")) return;
    try { await api.delete("/admin/settings/qr"); toast.success("Removed"); setHasQr(false); onChange && onChange(); }
    catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="flex items-start gap-4">
      <div className="w-40 h-40 rounded-md bg-slate-100 border border-slate-200 flex items-center justify-center overflow-hidden">
        {hasQr ? <img src={`${API}/settings/qr?_=${bump}`} alt="QR" className="w-full h-full object-contain" data-testid="qr-preview" /> : <QrCode className="w-8 h-8 text-slate-400" />}
      </div>
      <div className="flex-1">
        <Button variant="outline" size="sm" onClick={() => inp.current?.click()} disabled={busy} data-testid="upload-qr-btn"><Upload className="w-3 h-3 mr-1" /> {hasQr ? "Replace QR" : "Upload QR"}</Button>
        <input ref={inp} type="file" accept=".jpg,.jpeg,.png,.webp,image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
        {hasQr && <Button variant="outline" size="sm" onClick={remove} className="ml-2 text-red-600" data-testid="remove-qr-btn"><X className="w-3 h-3 mr-1" /> Remove</Button>}
        <p className="mt-2 text-xs text-slate-500">JPG / PNG / WEBP, max 4MB. GPay / PhonePe / Paytm — jo bhi QR ho.</p>
      </div>
    </div>
  );
};

